import { Delete, ErrorOutline, OpenInNew } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useCallback, useState } from "react";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";

import {
  GlobalOptOutSummaryQuery,
  OptOutList,
  TrackedJobStatus,
} from "~frontend/__generated__/graphql";
import Table from "~frontend/components/Table";
import { palette } from "~frontend/theme";

import { FlexBox } from "../FlexBox";
import { LinkButton } from "../Link";
import Loading from "../Loading";
import { OptOutListDetailModal } from "./OptOutListDetailModal";
import OptOutSearchDialog from "./OptOutSearchDialog";
import { useOptOutSummary } from "./useOptOutSummary";

export type List =
  GlobalOptOutSummaryQuery["globalOptOutSummary"]["optOutLists"][0];

type OptOutListTableProps = {
  subaccountId: string | null;
  type: OptOutList["type"];
  lists: List[];
  onClearFailedList: (list: List) => void;
};

const SmallLoadingIndicator = () => (
  <Loading size={20} style={{ margin: "6px" }} />
);

export const OptOutListTable = ({
  subaccountId,
  type,
  lists,
  onClearFailedList,
}: OptOutListTableProps): ReactElement => {
  const confirm = useConfirm();

  const [page, setPage] = useQueryParam(
    `${type.toLowerCase()}-page`,
    withDefault(NumberParam, 0)
  );
  const [pageSize, setPageSize] = useQueryParam(
    `${type.toLowerCase()}-pageSize`,
    withDefault(NumberParam, 10)
  );

  const [activeListDetail, setActiveListDetail] = useState<List | null>(null);
  const [activeNumberDetail, setActiveNumberDetail] = useState<string | null>(
    null
  );

  const { deleteList } = useOptOutSummary({ subaccountId });

  const handleDeleteList = useCallback(
    async (list: List) => {
      const { confirmed } = await confirm({
        title: (
          <>
            Delete <strong>{list.name}</strong>
          </>
        ),
        description: (
          <>
            Are you sure you want to delete <strong>{list.name}</strong>? Note
            that if contacts from this list appear on other Opt Out lists or
            strike lists, they won't be textable unless they are removed from
            those lists as well.
          </>
        ),
      });
      if (!confirmed) {
        return;
      }

      await deleteList(list.id);
    },
    [confirm, deleteList]
  );

  const columns = [
    {
      key: "name",
      title: "List",
      fullWidth: (row: List) => row.importJob?.status === "FAILED",
      renderValue: (row: List) => {
        const value = row.name;
        if (row.importJob?.status === "DONE") {
          return value;
        }

        if (row.importJob?.status === "FAILED") {
          return (
            <FlexBox sx={{ color: palette.red }} gap="6px">
              <ErrorOutline sx={{ fontSize: "16px" }} />

              <FlexBox direction="column">
                <Typography variant="body2">{value}</Typography>
                <Typography variant="body3">
                  {row.importJob?.errorMessage}.{" "}
                  <LinkButton
                    onClick={() => onClearFailedList(row)}
                    color="inherit"
                    sx={[
                      {
                        cursor: "pointer",
                        textDecorationColor: palette.red,
                      },
                      {
                        "&:hover": {
                          color: "#a31f2c",
                          textDecorationColor: "#a31f2c",
                        },
                      },
                    ]}
                  >
                    Clear and retry.
                  </LinkButton>
                </Typography>
              </FlexBox>
            </FlexBox>
          );
        }

        return (
          <Box sx={{ fontStyle: "italic", color: palette.darkGray }}>
            {value} (uploading)
          </Box>
        );
      },
    },
    {
      key: "addedBy",
      title: "Added By",
      sx: { width: 150 },
      renderValue: (row: List) => {
        return (
          <Box
            sx={{
              color:
                row.importJob?.status !== "DONE" ? palette.darkGray : undefined,
            }}
          >
            {row.createdByUser?.fullName}
          </Box>
        );
      },
    },
    {
      key: "addedAt",
      title: "Added At",
      sx: { width: 150 },
      renderValue: (row: List) =>
        row.importJob?.status === "DONE" ? (
          DateTime.fromMillis(row.createdAt).toLocaleString()
        ) : (
          <SmallLoadingIndicator />
        ),
    },
    {
      key: "expiresAt",
      title: type === "TEMPORARY" && "Expires At",
      sx: { width: 150 },
      renderValue: (row: List) => {
        if (type !== "TEMPORARY") {
          return null;
        }

        if (
          row.editExpirationJob?.status === TrackedJobStatus.Created ||
          row.editExpirationJob?.status === TrackedJobStatus.Queued ||
          row.editExpirationJob?.status === TrackedJobStatus.Running
        ) {
          return <SmallLoadingIndicator />;
        }

        return row.importJob?.status === "DONE" ? (
          DateTime.fromMillis(row.expiresAt).toLocaleString()
        ) : (
          <SmallLoadingIndicator />
        );
      },
    },
    {
      key: "count",
      title: "Opt Outs",
      sx: { width: 100 },
      renderValue: (row: List) => {
        if (row.importJob?.status !== "DONE") {
          return <SmallLoadingIndicator />;
        }

        return <strong>{row.count.toLocaleString()}</strong>;
      },
    },
  ];

  return (
    <>
      {activeListDetail && (
        <OptOutListDetailModal
          isOpen={true}
          listId={activeListDetail.id}
          onClose={() => setActiveListDetail(null)}
          onDeleteTrigger={() => {
            void handleDeleteList(activeListDetail);
          }}
          onShowNumberDetails={(phoneNumber: string) => {
            setActiveListDetail(null);
            setActiveNumberDetail(phoneNumber);
          }}
        />
      )}

      {activeNumberDetail && (
        <OptOutSearchDialog
          open={true}
          onClose={() => setActiveNumberDetail(null)}
          initialSearchValue={{
            type: "optedOutNumber",
            phoneNumber: activeNumberDetail,
          }}
        />
      )}

      <Table<List>
        columns={columns}
        rows={lists.slice(page * pageSize, (page + 1) * pageSize)}
        rowActions={(row: List) =>
          row.importJob?.status === "DONE"
            ? [
                {
                  id: "delete-list",
                  type: "button",
                  icon: <Delete />,
                  tooltipLabel: "Delete list",
                  onClick: async ({ id }) => {
                    await handleDeleteList(
                      lists.find(list => id === list.id) as OptOutList
                    );
                  },
                },
                {
                  id: "view-list",
                  type: "button",
                  icon: <OpenInNew />,
                  tooltipLabel: "View list details",
                  onClick: list => setActiveListDetail(list),
                },
              ]
            : []
        }
        totalCount={lists.length}
        alignRightAfterIdx={type === "PERMANENT" ? 2 : 3}
        actionSx={{ width: 100 }}
        rowsPerPage={pageSize}
        page={page}
        onChangePage={setPage}
        onChangeRowsPerPage={setPageSize}
      />
    </>
  );
};
